import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { loadDetail } from '../api/buckets.js';
import { PAPER_AR, memoryPaper, chosenPaper, setChosenPaper } from '../lib/paper.js';
import PaperPicker from './PaperPicker.jsx';
import ForgetButton from './ForgetButton.jsx';
import { useFavs, toggleFav } from '../lib/favs.js';
import '../modules/diary/Diary.css';

/* 点开一条记忆 = 铺开一张纸（10-05 铃：记忆/时间/日记打开统一成同一种纸）。
   原来是从底部弹出的小窗口；纸和翻旧页共用 Diary.css 的 .dr-reader / .dr-leaf。 */
const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

function fmt(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${WEEKDAYS[d.getDay()]} · ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// order = 列表里看到的顺序（id 数组）；给了就在纸底下画「上一条 / 下一条」，也能左右滑（10-05 铃）
export default function MemoryLeaf({ id, cache, onClose, live, onDeleted, order, onNav }) {
  const [b, setB] = useState(null);
  // 纸只挑一次：列表那份和详情那份字段不全一样（详情缺心情/日期会补默认值），
  // 以前两份各挑一次 → 点开先闪一张再换一张（10-05 铃报）。现在按列表那份定下就不换了。
  const [paper, setPaper] = useState(null);
  const [chosen, setChosen] = useState(null); // 她亲手换的纸（没换过 null → 按心情）
  const [dir, setDir] = useState(0);
  const favs = useFavs();
  const touch = useRef(null);
  const at = order && id ? order.indexOf(id) : -1;
  const prevId = at > 0 ? order[at - 1] : null;
  const nextId = at >= 0 && at < order.length - 1 ? order[at + 1] : null;
  function go(step) {
    const to = step < 0 ? prevId : nextId;
    if (!to || !onNav) return;
    setDir(step);
    onNav(to);
  }

  useEffect(() => {
    if (!id) {
      setB(null);
      return;
    }
    let cancelled = false;
    // 先用列表里那份（立刻铺纸），详情到了再换成完整正文
    setChosen(chosenPaper(id));
    const listed = (cache || []).find((x) => x.bucket_id === id) || null;
    setB(listed);
    setPaper(listed ? memoryPaper(listed) : null);
    // 列表已带全文（10-05 大脑改的）→ 直接铺，不再去要详情；只有列表里没有时才去要
    if (listed && listed.content) return undefined;
    loadDetail(id, cache).then((full) => {
      if (!cancelled && full) {
        setB(listed ? { ...full, created_at: full.created_at || listed.created_at } : full);
        setPaper((p) => p || memoryPaper(full));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [id, cache]);

  useEffect(() => {
    if (!id) return undefined;
    const onKey = (ev) => {
      if (ev.key === 'Escape') onClose();
      else if (ev.key === 'ArrowLeft') go(-1);
      else if (ev.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!id || !b || !paper) return null;
  const shown = chosen || paper;
  const star = b.weight >= 999;

  return createPortal(
    <div
      className="dr-reader"
      role="dialog"
      aria-label={b.title}
      onTouchStart={(ev) => {
        const t = ev.touches[0];
        touch.current = { x: t.clientX, y: t.clientY };
      }}
      onTouchEnd={(ev) => {
        const s0 = touch.current;
        touch.current = null;
        if (!s0) return;
        const t = ev.changedTouches[0];
        const dx = t.clientX - s0.x;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(t.clientY - s0.y) * 1.5) go(dx < 0 ? 1 : -1);
      }}
    >
      <div className="dr-reader-stage" onClick={(ev) => ev.target === ev.currentTarget && onClose()}>
        <article key={b.bucket_id} className={'dr-leaf ' + (dir > 0 ? 'from-right' : dir < 0 ? 'from-left' : 'opening')} data-paper={shown} style={{ '--ar': PAPER_AR[shown] }}>
          <PaperPicker
            value={shown}
            chosen={chosen}
            onPick={(k) => {
              setChosenPaper(b.bucket_id, k);
              setChosen(k);
            }}
          />
          {live && (
            <Ribbon on={favs.has(b.bucket_id)} onToggle={() => toggleFav(b.bucket_id)} />
          )}
          <div className="dr-leaf-in">
            <div className="dr-leaf-meta">
              <span>{fmt(b.created_at)}</span>
              <span className="dr-leaf-author">{star ? '✦ 恒星' : `MEMORIA · ${Math.round(b.importance)}/10`}</span>
            </div>
            <h3 className="dr-leaf-title">{b.title}</h3>
            {b.content && <div className="dr-leaf-text">{b.content}</div>}
            {b.tags?.length > 0 && (
              <div className="dr-leaf-tags">
                {b.tags.slice(0, 8).map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
            )}
            {onNav && (prevId || nextId) && (
              <nav className="dr-leaf-step">
                <button type="button" onClick={() => go(-1)} disabled={!prevId}>
                  <svg viewBox="0 0 24 12" aria-hidden="true"><path d="M23 6H2M7 1 2 6l5 5" /></svg>
                  上一条
                </button>
                <span aria-hidden="true">✦</span>
                <button type="button" onClick={() => go(1)} disabled={!nextId}>
                  下一条
                  <svg viewBox="0 0 24 12" aria-hidden="true"><path d="M1 6h21M17 1l5 5-5 5" /></svg>
                </button>
              </nav>
            )}
            {live && (
              <div className="dr-leaf-forget">
                <ForgetButton id={b.bucket_id} onDeleted={onDeleted} />
              </div>
            )}
          </div>
        </article>
      </div>
      <button type="button" className="dr-reader-close" onClick={onClose} aria-label="合上">
        <X size={18} strokeWidth={1.6} />
      </button>
    </div>,
    document.body,
  );
}

/* 珍藏书签（2026-10-16 铃）：纸顶上垂下一根丝带。没夹时只露一小截淡淡的线，
   点一下丝带垂下来、尾巴剪成燕尾，带一颗小 ♡——这篇就收进她的珍藏，他也会知道。 */
function Ribbon({ on, onToggle }) {
  return (
    <button
      type="button"
      className={'dr-ribbon' + (on ? ' on' : '')}
      onClick={(ev) => {
        ev.stopPropagation();
        onToggle();
      }}
      aria-pressed={on}
      aria-label={on ? '取下书签' : '夹一枚书签，收进珍藏'}
    >
      <svg viewBox="0 0 22 64" aria-hidden="true">
        <defs>
          <linearGradient id="dr-silk" x1="0" x2="1">
            <stop offset="0" stopColor="#9e2350" />
            <stop offset="0.45" stopColor="#d4507f" />
            <stop offset="0.6" stopColor="#e2759b" />
            <stop offset="1" stopColor="#a8295a" />
          </linearGradient>
        </defs>
        <path className="dr-ribbon-silk" d="M2 0h18v64l-9-8-9 8z" />
        <path className="dr-ribbon-heart" d="M11 47.5c-3.6-2.6-5.2-4.4-5.2-6.3 0-1.5 1.2-2.6 2.6-2.6 1.1 0 2 .6 2.6 1.5.6-.9 1.5-1.5 2.6-1.5 1.4 0 2.6 1.1 2.6 2.6 0 1.9-1.6 3.7-5.2 6.3z" />
      </svg>
    </button>
  );
}
